import { useCallback, useMemo } from 'react';
import { batch } from 'react-redux';
import Link from 'next/link';

import { IEquipmentTestDrive } from '@r1-frontend/api-domru/full-buy/v1/equipment/dto/EquipmentTestDrive.dto';

import format from '@r1-frontend/ui-react/components/costs/Cost/format';
import { TGuideItem } from '@r1-frontend/ui-react/components/Guide/GuideItem';
import { CloseBox } from '@r1-frontend/ui-react/components/svg/delivery';
import { Crown } from '@r1-frontend/ui-react/components/svg/main';
import { Like, MessageQuestion } from '@r1-frontend/ui-react/components/svg/support';
import ControlElement from '@r1-frontend/ui-react/components/typography/controlElement';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { ListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { NBSP } from '@r1-frontend/ui-react/tokens/symbols';
import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';
import { dayPluralize, monthPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import { IPurchaseOption } from '~/src/features/work-in-home-ab/components/Description/components/TestDrive/components/Details';
import { setIsShowDetails, setIsShowOptionSelect } from '~/src/features/work-in-home-ab/components/Description/components/TestDrive/context/actions';
import { useContextDispatch } from '~/src/features/work-in-home-ab/components/Description/components/TestDrive/context/hooks';

const pluralizeDays = getPluralize(dayPluralize);
const pluralizeMonths = getPluralize(monthPluralize);

interface IParams {
    testDrive?: IEquipmentTestDrive,
    purchaseOption?: IPurchaseOption,
}

export const useTestDriveGuide = ({ testDrive, purchaseOption }: IParams): TGuideItem[] => {
    const dispatch = useContextDispatch();

    const changeOptionHandler = useCallback(() => {
        batch(() => {
            dispatch(setIsShowDetails(false));
            dispatch(setIsShowOptionSelect(true));
        });
    }, []);

    const guideItems: TGuideItem[] = useMemo(() => {
        const { duration = 14, howItWorksText, includingSubscriptionsText } = testDrive || {};
        const { price = 0, value: leasingDuration } = purchaseOption || {};

        const items: TGuideItem[] = [];

        if (howItWorksText) {
            items.push({
                isActive: false,
                Icon: Crown,
                title: 'Как работает тест-драйв',
                description: howItWorksText,
                info: includingSubscriptionsText ? <div dangerouslySetInnerHTML={{ __html: includingSubscriptionsText }}/> : undefined,
            });
        }

        const formattedPrice = format(price, leasingDuration ? { monthly: true } : undefined);

        const textByLeasingDuration = leasingDuration
            ? <>
                &nbsp;&mdash; в&nbsp;рассрочку <b>{formattedPrice} на&nbsp;{leasingDuration}&nbsp;{pluralizeMonths(leasingDuration)}</b>
            </>
            : ` за${NBSP}${formattedPrice}`;

        items.push(
            {
                isActive: false,
                Icon: Like,
                title: `Кто установит и${NBSP}настроит`,
                description: 'Возьмите оборудование в&nbsp;тест-драйв, а&nbsp;об&nbsp;остальном позаботимся мы&nbsp;&mdash; приедем, бесплатно всё установим и&nbsp;настроим.',
            },
            {
                isActive: false,
                Icon: MessageQuestion,
                title: `Что произойдёт через ${duration}${NBSP}${pluralizeDays(duration)} тест-драйва`,
                description: <ListContainer $direction={'column'} $wrap={'nowrap'} $size={8}>
                    <Paragraph4 $color={COLORS.TextInfo}>
                        Когда тестовый период закончится, оборудование перейдёт к&nbsp;вам в&nbsp;собственность{textByLeasingDuration}.
                    </Paragraph4>
                    <ControlElement onClick={changeOptionHandler}>Изменить условия</ControlElement>
                </ListContainer>,
            },
            {
                isActive: false,
                Icon: CloseBox,
                title: 'Как работает возврат',
                description: <ListContainer $direction={'column'} $wrap={'nowrap'} $size={8}>
                    <Paragraph4 $color={COLORS.TextInfo}>
                        За&nbsp;3&nbsp;дня до&nbsp;окончания тестового периода вам придёт смс с&nbsp;напоминанием.
                        Вернуть оборудование можно в&nbsp;течение 14&nbsp;дней после завершения тест-драйва через Почту России.
                    </Paragraph4>
                    <Link href={'/faq/dostavka-oborudovaniya/kak-vernut-oborudovanie-cherez-pochtu-rossii-posle-test-drajva'} passHref>
                        <ControlElement as={'a'}>Подробные условия</ControlElement>
                    </Link>
                </ListContainer>,
            },
        );

        return items;
    }, [changeOptionHandler, purchaseOption, testDrive]);

    return guideItems;
};
